import * as React from "react"
import {
  Avatar,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Icon,
  IconButton,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Progress,
  Spinner,
  Stack,
  Tag as ChakraTag,
  Text,
  useDisclosure,
  useTheme,
} from "@chakra-ui/react"
import { Link as RLink } from "react-router-dom"
import { FiExternalLink } from "react-icons/fi"
import { FaCircle } from "react-icons/fa"

import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

import { BikeEntity } from "../lib/types/bike"
import { humanize } from "../lib/helpers/text"
import { LocationLink } from "./LocationLink"
import { BIKE_MARKER_COLORS, BIKE_MARKER_TEXT_COLORS } from "../lib/static/bike"
import { LiveChaseModal } from "./LiveChaseModal"
import { useFeatFlags } from "./providers/FeatFlagProvider"
import { useOrgs } from "./providers/OrgProvider"
import { useQuery } from "react-query"
import { MapBikeEntity } from "../lib/hooks/queries/useGetMapBikesQuery"
import { calcBikeStateColor } from "../lib/helpers/calcMarket"
import { getAssetIcon } from "../lib/static/asset"

dayjs.extend(relativeTime)

interface BikeMarkerProps {
  bike: MapBikeEntity
  isActive: boolean
}

export function BikeMarker({ bike, isActive }: BikeMarkerProps) {
  const theme = useTheme()
  const modalProps = useDisclosure()
  const { live_chase } = useFeatFlags()

  const ref = React.useRef<HTMLButtonElement | null>(null)

  React.useEffect(() => {
    // If Search returns a single Marker then simulate click on PopoverTrigger:
    if (isActive) {
      ref.current?.click()
    }
  }, [isActive])

  return (
    <>
      <Popover isLazy>
        <PopoverTrigger>
          <IconButton
            ref={ref}
            aria-label="asset marker"
            boxSize="40px"
            zIndex={1}
            borderRadius="50%"
            _hover={{ bg: theme.colors[BIKE_MARKER_COLORS[bike.state || "unknown"]]["500"] || "blue.500" }}
            overflow="hidden"
            bg={calcBikeStateColor(bike)}
            icon={<Icon as={getAssetIcon(bike.bicycle_type)} boxSize="18px" color="white" />}
          />
        </PopoverTrigger>

        <Portal>
          <PopoverContent w={350} zIndex={10000} bg="white">
            <MarkerContent uuid={bike.uuid} onOpenLiveChase={modalProps.onOpen} />
          </PopoverContent>
        </Portal>
      </Popover>
      {live_chase.visible && modalProps.isOpen && <LiveChaseModal {...modalProps} bikeId={bike.uuid} />}
    </>
  )
}

interface ContentProps {
  uuid: string
  onOpenLiveChase: () => void
}

function MarkerContent(props: ContentProps) {
  const { currentOrgs } = useOrgs()
  const { live_chase, tags, bike_sn_as_name_extension } = useFeatFlags()

  const openInNewTab = (url: string) => window.open(url, "_blank")

  const { data: bike, isLoading } = useQuery<BikeEntity, string>(`bicycles/${props.uuid}/`, {
    staleTime: Infinity,
  })

  if (isLoading)
    return (
      <Center h={200}>
        <Spinner />
      </Center>
    )

  if (!bike) return null

  const organization = currentOrgs.find(({ url }) => url === bike.organization)!
  return (
    <>
      <PopoverHeader>
        <Stack spacing={2}>
          <Flex align="center" justify="space-between">
            <Stack direction="row" spacing={2} align="center">
              <Avatar name={organization.name} src={organization.icon} size="xs" />
              <Link as={RLink} to={`/assets/${bike.uuid}`} fontSize="1.2rem">
                {bike.name}
                {bike.serial_number &&
                  bike_sn_as_name_extension?.visible &&
                  bike.name.toUpperCase() !== bike.serial_number.toUpperCase() &&
                  `.${bike.serial_number}`}
              </Link>
            </Stack>
            <IconButton
              aria-label="go to asset"
              icon={<FiExternalLink />}
              boxSize="30px"
              cursor="pointer"
              minW="30px"
              p={2}
              variant="ghost"
              onClick={() => openInNewTab(`/assets/${bike.uuid}`)}
            />
          </Flex>
          {bike.latitude && bike.longitude && (
            <LocationLink obj={{ latitude: bike.latitude, longitude: bike.longitude }} />
          )}
          <Flex align="center">
            <Button
              variant="outline"
              size="xs"
              onClick={() => openInNewTab(`/assets/${bike.uuid}/trips`)}
              mr={2}
            >
              See trips
            </Button>
            <Button
              variant="outline"
              size="xs"
              onClick={() => openInNewTab(`/bikes/${bike.uuid}/alerts`)}
              mr={2}
            >
              See alerts
            </Button>
            {live_chase.visible && (
              <Button
                variant="outline"
                size="xs"
                px={3}
                leftIcon={<Box as={FaCircle} boxSize="10px" color="red.500" />}
                onClick={props.onOpenLiveChase}
              >
                Live chase
              </Button>
            )}
          </Flex>
        </Stack>
      </PopoverHeader>
      <PopoverBody p={0}>
        {bike.description && (
          <>
            <Stack spacing={0} px={3} py={1}>
              <Text color="gray.800" fontSize="0.8rem">
                {bike.description}
              </Text>
            </Stack>
            <Divider />
          </>
        )}

        <Stack spacing={0} px={3} py={1}>
          <Text color="gray.800" fontSize="0.8rem">
            State:{" "}
            <Text as="span" color={BIKE_MARKER_TEXT_COLORS[bike.state]}>
              {humanize(bike.state)}
            </Text>
          </Text>
          <Text color="gray.800" fontSize="0.8rem">
            Tracker ID:{" "}
            <Text as="span" color="gray.600">
              {bike.bleid || "N/A"}
            </Text>
          </Text>
          <Text color="gray.800" fontSize="0.8rem">
            Tracker paired:{" "}
            <Text as="span" color="gray.600">
              {bike.devices?.tracker?.paired_at
                ? dayjs(bike.devices?.tracker?.paired_at).format("MM/DD/YYYY")
                : "N/A"}
            </Text>
          </Text>
          {bike.devices?.lock && bike.devices?.lock.paired_at && (
            <Text color="gray.800" fontSize="0.8rem">
              Lock paired{" "}
              <Text as="span" color="gray.600">
                {dayjs(bike.devices?.lock?.paired_at).format("MM/DD/YYYY")}
              </Text>
            </Text>
          )}
        </Stack>

        <Divider />

        {tags.visible && bike.tags.length > 0 && (
          <>
            <Box px={2} py={1}>
              {bike.tags.map((tag) => (
                <ChakraTag bg={tag.tag_declaration_color} key={tag.uuid} m={1}>
                  {tag.tag_declaration_name}
                </ChakraTag>
              ))}
            </Box>
            <Divider />
          </>
        )}

        <Stack spacing={2} px={3} py={1}>
          <Flex align="center" justify="space-between">
            <Flex align="center">
              <Text fontSize="0.7rem">{Math.ceil(bike.state_of_charge || 0)}%</Text>
              <Progress
                size="sm"
                ml={1}
                h="5px"
                borderRadius="md"
                colorScheme={
                  !bike.state_of_charge || bike.state_of_charge < 10
                    ? "red"
                    : bike.state_of_charge < 50
                    ? "orange"
                    : "green"
                }
                w="120px"
                value={bike.state_of_charge || 0}
              />
            </Flex>
            <Flex align="center" textAlign="right">
              <Text fontSize="0.7rem">Updated:</Text>
              <Text fontSize="0.7rem" ml={1} color="gray.600" whiteSpace="nowrap">
                {bike.last_cellular_update ? dayjs(bike.last_cellular_update).fromNow() : "N/A"}
              </Text>
            </Flex>
          </Flex>
        </Stack>
      </PopoverBody>
    </>
  )
}
